import { COST_RATES as R } from './costRates';

/**
 * Compute all SOW cost line items from a full deal object.
 * Returns [{section, label, estimated}] — estimated in whole dollars.
 */
export function computeSOWCosts(deal) {
  const lines = [];
  const add = (section, label, amount) => {
    const v = Math.round(amount || 0);
    if (v > 0) lines.push({ section, label, estimated: v });
  };

  // ── Demo & Disposal ────────────────────────────────────────────────────────
  const demo = deal.sow_demo || {};
  if (demo.dumpster_count > 0) {
    const rate = R.dumpster[demo.dumpster_size] || R.dumpster['20yd'];
    add('demo', `Dumpster (${demo.dumpster_count}× ${demo.dumpster_size || '20yd'})`, demo.dumpster_count * rate);
  }
  if (demo.full_cleanout) add('demo', 'Full Cleanout',            R.full_cleanout);
  if (demo.haul_away)     add('demo', 'Haul Away / Junk Removal', R.haul_away);
  if (demo.demo_labor > 0) add('demo', 'Demo Labor',              demo.demo_labor);

  // ── Roof ──────────────────────────────────────────────────────────────────
  const sys = deal.sow_systems || {};
  if (sys.roof_action === 'replace' && sys.roof_sq_ft) {
    const rate = R.roof[sys.roof_type] || R.roof.shingle;
    add('roof', `Roof Replace — ${sys.roof_type || 'shingle'} (${sys.roof_sq_ft} sqft)`, sys.roof_sq_ft * rate);
  } else if (sys.roof_action === 'repair') {
    add('roof', 'Roof Repair', R.roof_repair);
  }

  // ── Plumbing ──────────────────────────────────────────────────────────────
  if (sys.plumbing_action === 'repipe') {
    add('plumbing', 'Full Repipe — Supply', R.repipe_supply);
  } else if (sys.plumbing_action === 'repair') {
    add('plumbing', 'Plumbing Repairs',     R.plumbing_repair);
  }
  if (sys.wh_action === 'replace') {
    add('plumbing', `Water Heater Replace (${sys.wh_type || 'electric'})`, R.wh_replace);
  }

  // ── Electrical ────────────────────────────────────────────────────────────
  if (sys.panel_action === 'replace') add('electrical', 'Panel Replace',       R.panel_replace);
  if (sys.panel_action === 'upgrade') add('electrical', 'Panel Upgrade',        R.panel_upgrade);
  if (sys.wiring_type === 'aluminum' && !sys.alimicon_present && sys.alimicon_cost > 0) {
    add('electrical', 'Alimicon Connectors (aluminum wiring)', sys.alimicon_cost);
  }

  // ── HVAC ──────────────────────────────────────────────────────────────────
  if (sys.hvac_action === 'replace') {
    const rate = R.hvac_replace[sys.hvac_tons] || R.hvac_replace_default;
    add('hvac', `HVAC Replace (${sys.hvac_tons || '?'} ton)`, rate);
  } else if (sys.hvac_action === 'service') {
    add('hvac', 'HVAC Service / Clean', R.hvac_service);
  }

  // ── Foundation ────────────────────────────────────────────────────────────
  if (sys.foundation_action === 'repair') {
    add('foundation', 'Foundation Repair', R.foundation_repair);
  }

  // ── Exterior — Windows ────────────────────────────────────────────────────
  const ext = deal.sow_exterior || {};
  if (ext.window_action && ext.window_action !== 'keep' && ext.window_count > 0) {
    const count = ext.window_action === 'replace_partial'
      ? Math.ceil(ext.window_count / 2)
      : ext.window_count;
    add('exterior_windows', `Windows Replace (${count})`, count * R.window_replace);
  }

  // ── Exterior — Siding ─────────────────────────────────────────────────────
  if (ext.siding_action && ext.siding_action !== 'keep' && ext.siding_sq_ft) {
    add('exterior_siding', `Siding ${ext.siding_action} (${ext.siding_sq_ft} sqft)`,
      ext.siding_sq_ft * R.siding_replace_per_sqft);
  }

  // ── Exterior — Paint ──────────────────────────────────────────────────────
  if (ext.ext_paint_action && ext.ext_paint_action !== 'keep' && ext.ext_paint_sq_ft) {
    add('exterior_paint', `Exterior Paint (${ext.ext_paint_sq_ft} sqft)`,
      ext.ext_paint_sq_ft * R.ext_paint_per_sqft);
  }

  // ── Exterior — Gutters ────────────────────────────────────────────────────
  if (ext.gutters_action && !['keep','none',''].includes(ext.gutters_action) && ext.gutters_linear_ft) {
    if (ext.gutters_action === 'demo') {
      add('exterior_gutters', `Gutters Demo / Remove (${ext.gutters_linear_ft} lft)`,
        ext.gutters_linear_ft * (R.gutters_demo_per_lft || 2));
    } else {
      add('exterior_gutters', `Gutters ${ext.gutters_action} (${ext.gutters_linear_ft} lft)`,
        ext.gutters_linear_ft * R.gutters_per_lft);
    }
  }

  // ── Exterior — Pressure Wash ──────────────────────────────────────────────
  if (ext.pressure_wash && ext.pressure_wash_sq_ft) {
    add('exterior_pressure_wash', `Pressure Wash (${ext.pressure_wash_sq_ft} sqft)`,
      ext.pressure_wash_sq_ft * R.pressure_wash_per_sqft);
  }

  // ── Landscape ─────────────────────────────────────────────────────────────
  const land = deal.sow_landscape || {};
  if (land.landscape_scope && land.landscape_scope !== 'cleanup') {
    add('landscape', `Landscape — ${land.landscape_scope.replace(/_/g, ' ')}`,
      R.landscape[land.landscape_scope] || 0);
  } else if (land.landscape_scope === 'cleanup') {
    add('landscape', 'Landscape Cleanup', R.landscape.cleanup);
  }
  if (land.sod && land.sod_sq_ft) {
    add('landscape', `Sod (${land.sod_sq_ft} sqft)`, land.sod_sq_ft * R.sod_per_sqft);
  }
  if (land.tree_removal_count > 0) {
    add('landscape', `Tree Removal (${land.tree_removal_count})`, land.tree_removal_count * R.tree_removal);
  }
  if (land.irrigation === 'new')    add('landscape', 'Irrigation — New System', R.irrigation_new);
  if (land.irrigation === 'repair') add('landscape', 'Irrigation — Repair',     R.irrigation_repair);

  // ── Fence ─────────────────────────────────────────────────────────────────
  if (land.fence_action && land.fence_action !== 'none' && land.fence_linear_ft) {
    const rate = R.fence[land.fence_type] || R.fence.wood;
    add('fence', `Fence ${land.fence_action} — ${land.fence_type || 'wood'} (${land.fence_linear_ft} lft)`,
      land.fence_linear_ft * rate);
  }

  // ── Driveway ──────────────────────────────────────────────────────────────
  if (land.driveway_action === 'repair') {
    add('driveway', 'Driveway Repair / Seal', R.driveway_repair);
  } else if (land.driveway_action === 'replace' && land.driveway_sq_ft) {
    const rate = R.driveway[land.driveway_material] || R.driveway.concrete;
    add('driveway', `Driveway Replace — ${land.driveway_material || 'concrete'} (${land.driveway_sq_ft} sqft)`,
      land.driveway_sq_ft * rate);
  }

  // ── Rooms ─────────────────────────────────────────────────────────────────
  for (const room of (deal.rooms || [])) {
    const roomLines = computeRoomCosts(room);
    roomLines.forEach(({ label, amount }) => {
      lines.push({ section: `room_${room.id}`, label, estimated: amount });
    });
  }

  return lines;
}

/**
 * Compute cost line items for a single room.
 * Returns [{label, amount}].
 */
export function computeRoomCosts(room) {
  const lines = [];
  const add = (label, amount) => {
    const v = Math.round(amount || 0);
    if (v > 0) lines.push({ label, amount: v });
  };

  const sqft = room.sq_ft || (room.length_ft && room.width_ft ? room.length_ft * room.width_ft : 0);

  // Flooring
  if (room.flooring_condition === 'replace' && sqft) {
    const rate = R.flooring[room.flooring_type] || R.flooring.LVP;
    add(`Flooring — ${room.flooring_type || 'LVP'} (${sqft} sqft)`, sqft * rate);
  }

  // Paint walls: estimate wall area from sqft (assume square room)
  if (room.paint_walls && sqft) {
    const side = Math.sqrt(sqft);
    const wallArea = side * 4 * (room.ceiling_height || 8);
    add('Paint — Walls', wallArea * R.paint_walls_per_sqft);
  }

  // Drywall
  if (room.drywall_damage === 'minor') add('Drywall Repair — Minor', R.drywall_minor);
  if (room.drywall_damage === 'major') add('Drywall Repair — Major', R.drywall_major);

  // Paint ceiling
  if (room.paint_ceiling && sqft) {
    add('Paint — Ceiling', sqft * R.paint_ceiling_per_sqft);
  }

  // Electrical fixtures
  if (room.light_fixtures > 0) add(`Light Fixtures (${room.light_fixtures})`, room.light_fixtures * R.light_fixture);
  if (room.ceiling_fans > 0)   add(`Ceiling Fans (${room.ceiling_fans})`,     room.ceiling_fans   * R.ceiling_fan);

  // Doors
  if (room.door_condition === 'replace' && room.door_count > 0) {
    add(`Interior Doors Replace (${room.door_count})`, room.door_count * R.door_replace);
  }

  // Kitchen cabinets
  if (room.cabinet_condition === 'paint') {
    add('Cabinet Paint', R.cabinet_paint);
  } else if (room.cabinet_condition === 'reface') {
    add('Cabinet Reface', R.cabinet_reface);
  } else if (room.cabinet_condition === 'replace' && room.cabinet_linear_ft) {
    add(`Cabinets Replace (${room.cabinet_linear_ft} lft)`, room.cabinet_linear_ft * R.cabinet_replace_per_lft);
  }

  // Kitchen countertop
  if (room.countertop_type && room.countertop_sq_ft) {
    const rate = R.countertop[room.countertop_type] || R.countertop.laminate;
    add(`Countertop — ${room.countertop_type} (${room.countertop_sq_ft} sqft)`, room.countertop_sq_ft * rate);
  }

  // Kitchen sink
  if (room.sink_condition === 'replace') add('Sink Replace', R.sink_replace);

  // Appliances
  const appFields = [
    ['appliance_fridge', 'fridge', 'Fridge'],
    ['appliance_stove', 'stove', 'Stove'],
    ['appliance_dishwasher', 'dishwasher', 'Dishwasher'],
    ['appliance_microwave', 'microwave', 'Microwave'],
    ['appliance_disposal', 'disposal', 'Disposal'],
  ];
  for (const [field, key, lbl] of appFields) {
    if (room[field] === 'replace' || room[field] === 'new') {
      add(`Appliance — ${lbl}`, R.appliance[key]);
    }
  }

  // Bathroom
  if (room.toilet_condition === 'replace') add('Toilet Replace', R.toilet_replace);

  if (room.vanity_condition === 'paint') {
    add('Vanity Paint', R.vanity_paint);
  } else if (room.vanity_condition === 'replace') {
    add(`Vanity Replace (${room.vanity_size || 'single'})`,
      room.vanity_size === 'double' ? R.vanity_replace_double : R.vanity_replace_single);
  }

  if (room.vanity_countertop_type) {
    const rate = R.vanity_countertop[room.vanity_countertop_type];
    if (rate) add(`Vanity Countertop — ${room.vanity_countertop_type}`, rate);
  }

  if (room.tub_shower_condition === 'retile' && room.tile_sq_ft) {
    add(`Tub/Shower Retile (${room.tile_sq_ft} sqft)`, room.tile_sq_ft * R.tub_retile_per_sqft);
  } else if (room.tub_shower_condition === 'replace') {
    add('Tub/Shower Replace', R.tub_replace);
  }

  if (room.exhaust_fan_condition === 'replace') add('Exhaust Fan Replace', R.exhaust_fan_replace);

  return lines;
}

/**
 * Given sow_costs DB rows and computed lines, return budgeted/actual totals.
 */
export function sowTotals(sowCosts, computedLines) {
  // Build a lookup from existing DB rows
  const dbMap = {};
  for (const row of (sowCosts || [])) {
    dbMap[`${row.section}|||${row.label}`] = row;
  }

  let budgeted = 0;
  let actual   = 0;

  for (const line of computedLines) {
    const key = `${line.section}|||${line.label}`;
    const row = dbMap[key];
    // If user has overridden budgeted, use that; otherwise use the calculated estimate
    budgeted += row ? (row.budgeted ?? line.estimated) : line.estimated;
    actual   += row ? (row.actual || 0) : 0;
  }

  return { budgeted, actual };
}
